import {isEmpty} from 'lodash'
import React, {useCallback, useContext, useEffect, useRef, useState} from 'react'
import {useAuth} from '../../../app/modules/auth'
import CarrierService from '../../../app/modules/common/carriers/core/_requests'
import SettingsService from '../../../app/modules/settings/core/_requests'
import {DEFAULT_PAGE, PAGE_SIZE_MAX} from '../../constants'
import {HAZMAT_SHOWN_KEY} from '../../constants/_hazmat'
import TableService from '../../partials/widgets/tables/core/_requests'
import {StorageHelpers} from '../StorageHelpers'
import OrderService from '../../../app/modules/orders/core/_requests'
import {SYNC_STATUS} from '../../../app/modules/orders/core/_const'
import AutomationRulesService from '../../../app/modules/settings/core/AutomationRulesService'

const STATES_GLOBAL_STORAGE_KEY =
  process.env.REACT_APP_STATES_GLOBAL_STORAGE_KEY || 'STATES_GLOBAL_STORAGE_KEY'

export type SyncStatus = typeof SYNC_STATUS[keyof typeof SYNC_STATUS]
type Store = {
  id: string
  name: string
  status: SyncStatus
  error?: string
}

type Job = {
  jobId: string
  stores: Store[]
}

type SyncState =
  | {type: typeof SYNC_STATUS.IDLE; jobs: Job[]; shouldReload: boolean; retryDone: boolean}
  | {type: typeof SYNC_STATUS.SYNCING; jobs: Job[]; shouldReload: boolean; retryDone: boolean}
  | {type: typeof SYNC_STATUS.FAILED; jobs: Job[]; shouldReload: boolean; retryDone: boolean}
  | {type: typeof SYNC_STATUS.SUCCEEDED; jobs: Job[]; shouldReload: boolean; retryDone: boolean}
  | {type: typeof SYNC_STATUS.RETRYING; jobs: Job[]; shouldReload: boolean; retryDone: boolean}
  | {type: typeof SYNC_STATUS.CANCELLED; jobs: Job[]; shouldReload: boolean; retryDone: boolean}

export interface CarrierRouteSettings {
  carriers: Record<string, boolean>
  hide_no_rates: boolean
}

export interface StatesGlobalContextModel {
  statesGlobal: any
  getAddress: any
  getPackages: any
  getPresets: any
  setHazmat: any
  getManageColumns: any
  getCarrierRouteSettings: any
  getAutomationRulesMetadata: any
  syncState: any
  setSyncState: any
  startSync: (storeType?: string, storeId?: string) => Promise<void>
  retrySync: () => Promise<void>
  cancelSync: () => Promise<void>
}

const initStatesGlobalContextPropsStates = {
  statesGlobal: StorageHelpers.getItemLocalStorage(STATES_GLOBAL_STORAGE_KEY),
  getAddress: () => {},
  getPackages: () => {},
  getPresets: () => {},
  setHazmat: () => {},
  getManageColumns: () => {},
  getCarrierRouteSettings: () => {},
  getAutomationRulesMetadata: () => {},
  syncState: {type: SYNC_STATUS.IDLE, jobs: [], shouldReload: false, retryDone: false},
  setSyncState: () => {},
  startSync: async () => {},
  retrySync: async () => {},
  cancelSync: async () => {},
}

const StatesGlobalContext = React.createContext<StatesGlobalContextModel>(
  initStatesGlobalContextPropsStates
)

const StatesGlobalProvider: React.FC = ({children}) => {
  const {currentUser} = useAuth()
  let [statesGlobal, setStateGlobal] = useState(
    StorageHelpers.getItemLocalStorage(STATES_GLOBAL_STORAGE_KEY)
  )
  const [carriers, setCarriers] = useState<Object>(statesGlobal?.carriers)
  const [addOnServices, setAddOnServices] = useState<Object>(statesGlobal?.addOnServices)
  const [carriersPackage, setCarriersPackage] = useState<Object>(statesGlobal?.carriersPackage)
  const [supportedMultiParcelServices, setSupportedMultiParcelServices] = useState<string[]>(
    statesGlobal?.supportedMultiParcelServices
  )
  const [address, setAddress] = useState<Object>(statesGlobal?.address)
  const [packages, setPackages] = useState<Object>(statesGlobal?.packages)
  const [presets, setPresets] = useState<Object>(statesGlobal?.presets)
  const [manageColumns, setManageColumns] = useState<Object>(statesGlobal?.manageColumns)
  const [carrierRouteSettings, setCarrierRouteSettings] = useState<
    CarrierRouteSettings | undefined
  >(statesGlobal?.carrierRouteSettings)
  const [automationRulesMetadata, setAutomationRulesMetadata] = useState<any>(
    statesGlobal?.automationRulesMetadata
  )
  const [hazmat, setHazmat] = useState<boolean>(() => {
    if (StorageHelpers.getItemLocalStorage(HAZMAT_SHOWN_KEY)) {
      return false
    }
    return statesGlobal?.hazmat || (currentUser?.show_reminder ?? true)
  })

  const handleSetHazmat = (value: boolean) => {
    if (!value) {
      StorageHelpers.setItemLocalStorage(HAZMAT_SHOWN_KEY, true)
    }
    setHazmat(value)
  }

  // begin: sync on all store views
  const [syncState, setSyncState] = useState<SyncState>(() => {
    const syncStateSaved = StorageHelpers.getItemLocalStorage('SYNC_STATE')

    return (
      (syncStateSaved as SyncState) || {
        type: SYNC_STATUS.IDLE,
        jobs: [],
        shouldReload: false,
        retryDone: false,
      }
    )
  })
  const pollingRef = useRef<NodeJS.Timeout | null>(null)
  const isCancelledRef = useRef(false)
  const shouldStartRetryPollingRef = useRef(false)

  /**
   * Starts the synchronization process across all stores by creating sync jobs
   */
  const startSync = async (storeType?: string, storeId?: string) => {
    // reset flag cancel when start a new period sync
    isCancelledRef.current = false

    try {
      const payload = storeType ? {store_type: storeType, store_id: storeId} : {}
      const response = await OrderService.createSyncJob(payload)
      const newState: SyncState = {
        type:
          response.status === 'success' && response?.jobs?.length
            ? SYNC_STATUS.SYNCING
            : SYNC_STATUS.IDLE,
        jobs:
          response?.jobs?.map((job) => ({
            jobId: job.jobId,
            stores: job.stores,
          })) ?? [],
        shouldReload: false,
        retryDone: false,
      }
      setSyncState(newState)
    } catch (error) {
      console.error(error)
    }
  }

  /**
   * Retries syncing only the stores that previously failed.
   */
  const retrySync = async () => {
    try {
      // Stop polling
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
        pollingRef.current = null
      }

      // Reset flag cancel
      isCancelledRef.current = false
      shouldStartRetryPollingRef.current = false

      setSyncState((prev) => ({...prev, type: SYNC_STATUS.RETRYING, retryDone: false}))

      const response = await OrderService.retrySyncJob({
        job_ids: (syncState.jobs ?? []).map((j) => j.jobId),
      })

      shouldStartRetryPollingRef.current = true
      setSyncState((prev) => ({...prev, jobs: response.jobs, retryDone: true}))
    } catch (error) {
      console.error('Failed to retry sync:', error)
    }
  }

  /**
   * Cancels all active sync jobs and updates the sync state to "Cancelled"
   */
  const cancelSync = async () => {
    isCancelledRef.current = true
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }

    setSyncState((prev) => ({...prev, type: SYNC_STATUS.CANCELLED, shouldReload: true}))
    try {
      await OrderService.cancelSyncJob({
        job_ids: (syncState.jobs ?? []).map((j) => j.jobId),
      })
    } catch (error) {
      console.error(error)
    }
  }

  // Polling for sync status
  useEffect(() => {
    if (
      syncState.type === SYNC_STATUS.IDLE ||
      syncState.type === SYNC_STATUS.SUCCEEDED ||
      syncState.type === SYNC_STATUS.CANCELLED ||
      !syncState.jobs?.length
    ) {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
        pollingRef.current = null
      }
      return
    }

    if (syncState.type === SYNC_STATUS.RETRYING && !shouldStartRetryPollingRef.current) {
      return
    }

    shouldStartRetryPollingRef.current = false

    pollingRef.current = setInterval(async () => {
      if (isCancelledRef.current) {
        if (pollingRef.current) {
          clearInterval(pollingRef.current)
          pollingRef.current = null
        }
        return
      }

      try {
        const response = await OrderService.getJobStatus({
          jobIds: syncState.jobs.map((j) => j.jobId),
        })

        if (isCancelledRef.current) return

        const stopPolling = !response.jobs.some((job) =>
          job.stores.some((store) => !store.finished_at || store.status === 'processing')
        )

        const updatedState: SyncState = {
          type: stopPolling ? SYNC_STATUS.SUCCEEDED : SYNC_STATUS.SYNCING,
          jobs: response.jobs,
          shouldReload: stopPolling,
          retryDone: false,
        }

        setSyncState(updatedState)

        if (!response.jobs?.length || stopPolling) {
          if (pollingRef.current) {
            clearInterval(pollingRef.current)
            pollingRef.current = null
          }
        }
      } catch (err) {
        console.error(err)
        if (pollingRef.current) {
          clearInterval(pollingRef.current)
          pollingRef.current = null
        }
      }
    }, 5000)

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
        pollingRef.current = null
      }
    }
  }, [syncState.type, syncState.jobs])

  useEffect(() => {
    StorageHelpers.setItemLocalStorage('SYNC_STATE', syncState)
  }, [syncState])
  // end: sync on all store views

  // begin: state global carriers
  const getCarriers = useCallback(async () => {
    try {
      const response = await CarrierService.getAll()
      if (response) {
        setCarriers(response.carriers)
        setCarriersPackage(response.package_types)
        setSupportedMultiParcelServices(response.supported_multi_parcel_services)
        setAddOnServices(response.add_on_services)
      }
    } catch (error) {
      console.error(error)
    }
  }, [])

  useEffect(() => {
    if (currentUser && (isEmpty(carriers) || isEmpty(carriersPackage))) {
      getCarriers()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCarriers, currentUser])
  // end: state global carriers

  // begin: state global address
  const getAddress = useCallback(async (nameAddress = 'from') => {
    try {
      const {shipping_addresses}: any = await SettingsService.getAddress({
        params: {page: DEFAULT_PAGE, page_size: PAGE_SIZE_MAX, type: nameAddress},
      })

      if (shipping_addresses) {
        setAddress((prev) => ({...prev, [nameAddress]: shipping_addresses}))
      }
    } catch (error) {
      console.error(error)
    }
  }, [])

  useEffect(() => {
    if (currentUser && isEmpty(address)) {
      getAddress('from')
      getAddress('to')
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAddress, currentUser])
  // end: state global address

  // begin: state global packages
  const getPackages = useCallback(async () => {
    try {
      const config = {
        params: {
          page: DEFAULT_PAGE,
          page_size: PAGE_SIZE_MAX,
        },
      }
      const {packages} = await SettingsService.getPackages(config)
      if (packages) {
        setPackages(packages)
      }
    } catch (error) {
      console.error(error)
    }
  }, [])

  useEffect(() => {
    if (currentUser && isEmpty(packages)) {
      getPackages()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getPackages, currentUser])
  // end: state global packages

  // begin: state global presets
  const getPresets = useCallback(async () => {
    try {
      const config = {
        params: {
          page: DEFAULT_PAGE,
          page_size: PAGE_SIZE_MAX,
        },
      }

      const {shipping_presets} = await SettingsService.getShippingPresets(config)
      if (shipping_presets) {
        setPresets(shipping_presets)
      }
    } catch (error) {
      console.error(error)
    }
  }, [])

  useEffect(() => {
    if (currentUser && isEmpty(presets)) {
      getPresets()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getPresets, currentUser])
  // end: state global presets

  // begin: state global custom column
  const getManageColumns = useCallback(async () => {
    const config = {
      params: {
        filter_key: 'custom_columns',
      },
    }
    try {
      const {filters} = await TableService.getCustomColumns(config)
      if (filters.length > 0) {
        setManageColumns(
          filters?.reduce((allDataColumn, currentColumn) => {
            allDataColumn[currentColumn?.filter_name] = currentColumn?.filter_payload
            return allDataColumn
          }, {})
        )
      } else {
        setManageColumns({})
      }
    } catch (error) {
      console.error(error)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (currentUser && isEmpty(manageColumns)) {
      getManageColumns()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getManageColumns, currentUser])
  // end: state global users

  // begin: state global carrier route settings
  const getCarrierRouteSettings = useCallback(async () => {
    try {
      const response = await SettingsService.getCarrierRoute()
      const data = response?.shipping_carrier_and_route
      if (data) {
        setCarrierRouteSettings(data)
      }
    } catch (error) {
      console.error(error)
    }
  }, [])

  useEffect(() => {
    if (currentUser && isEmpty(carrierRouteSettings)) {
      getCarrierRouteSettings()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getCarrierRouteSettings, currentUser])
  // end: state global carrier route settings

  const getAutomationRulesMetadata = useCallback(async () => {
    try {
      const response = await AutomationRulesService.getAutomationRulesMetadata()
      if (response) {
        setAutomationRulesMetadata(response)
      }
    } catch (error) {
      console.error('Error fetching automation rules metadata:', error)
    }
  }, [])

  useEffect(() => {
    if (currentUser && isEmpty(automationRulesMetadata)) {
      getAutomationRulesMetadata()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAutomationRulesMetadata, currentUser])
  // end: state global automation rules metadata

  // Update global state when making the first API call.
  useEffect(() => {
    StorageHelpers.setItemLocalStorage(STATES_GLOBAL_STORAGE_KEY, {
      carriers: carriers,
      carriersPackage: carriersPackage,
      supportedMultiParcelServices: supportedMultiParcelServices,
      address: address,
      packages: packages,
      presets: presets,
      hazmat: hazmat,
      manageColumns: manageColumns,
      addOnServices,
      carrierRouteSettings,
      automationRulesMetadata,
    })

    setStateGlobal(StorageHelpers.getItemLocalStorage(STATES_GLOBAL_STORAGE_KEY))
  }, [
    address,
    carriers,
    packages,
    presets,
    hazmat,
    manageColumns,
    carriersPackage,
    supportedMultiParcelServices,
    addOnServices,
    carrierRouteSettings,
    automationRulesMetadata,
  ])

  return (
    <StatesGlobalContext.Provider
      value={{
        statesGlobal,
        getAddress,
        getPackages,
        getPresets,
        setHazmat: handleSetHazmat,
        getManageColumns,
        getCarrierRouteSettings,
        getAutomationRulesMetadata,
        syncState,
        setSyncState,
        startSync,
        retrySync,
        cancelSync,
      }}
    >
      {children}
    </StatesGlobalContext.Provider>
  )
}

function useStatesGlobal() {
  return useContext(StatesGlobalContext)
}

export {StatesGlobalProvider, useStatesGlobal}
